import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import { certificatesAPI } from '../services/api';

export default function VerifyCertificate() {
  const [sortBy, setSortBy] = useState('srno');
  const [search, setSearch] = useState('');
  const [certificateData, setCertificateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await certificatesAPI.getAll();
      const certs = response.data.certificates.map(cert => ({
        name: cert.holder_name,
        date: cert.date_issued,
        degree: cert.degree,
        number: cert.certificate_number
      }));
      setCertificateData(certs);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  // Sorting logic
  const sortedData = [...certificateData]
    .filter(cert => cert.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(a.date) - new Date(b.date);
      } else if (sortBy === 'srno') {
        const numA = parseInt(a.number.replace(/\D/g, '').slice(-2));
        const numB = parseInt(b.number.replace(/\D/g, '').slice(-2));
        return numA - numB;
      }
      return 0;
    });

  return (
    <>
      <Header />
      <div style={{ 
        padding: '2rem', 
        maxWidth: '1200px', 
        margin: '0 auto',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#333', 
          marginBottom: '2rem',
          fontSize: '2rem'
        }}>
          Certificate Verification
        </h2>
        
        {error && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#666'
          }}>
            Loading certificates...
          </div>
        )}
        
        {!loading && !error && (
          <>
            <div style={{ 
              marginBottom: '1.5rem', 
              display: 'flex', 
              gap: '1rem', 
              flexWrap: 'wrap',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: 'bold',
                  color: '#555'
                }}>
                  Search by Name
                </label>
                <input
                  type="text"
                  placeholder="Enter name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '10px', 
                    fontSize: '1em',
                    border: '2px solid #ddd',
                    borderRadius: '4px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={e => e.target.style.borderColor = '#4CAF50'}
                  onBlur={e => e.target.style.borderColor = '#ddd'}
                />
              </div>
              
              <div style={{ minWidth: '200px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: 'bold',
                  color: '#555'
                }}>
                  Sort By
                </label>
                <select 
                  value={sortBy} 
                  onChange={e => setSortBy(e.target.value)} 
                  style={{ 
                    width: '100%',
                    padding: '10px', 
                    fontSize: '1em',
                    border: '2px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="srno">📋 Serial Number</option>
                  <option value="date">📅 Date Issued</option>
                </select>
              </div>
            </div>

            <div style={{ overflowX: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#4CAF50', color: 'white' }}>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Sr.No</th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Name</th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Date Issued</th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Degree</th>
                    <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Certificate Number</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.length > 0 ? (
                    sortedData.map((cert, idx) => (
                      <tr 
                        key={idx}
                        style={{ 
                          backgroundColor: idx % 2 === 0 ? '#f9f9f9' : 'white',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#f9f9f9' : 'white'}
                      >
                        <td style={{ border: '1px solid #ddd', padding: '12px' }}>{idx + 1}</td>
                        <td style={{ border: '1px solid #ddd', padding: '12px', fontWeight: '500' }}>{cert.name}</td>
                        <td style={{ border: '1px solid #ddd', padding: '12px' }}>{cert.date}</td>
                        <td style={{ border: '1px solid #ddd', padding: '12px' }}>{cert.degree}</td>
                        <td style={{ border: '1px solid #ddd', padding: '12px', fontFamily: 'monospace' }}>{cert.number}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td 
                        colSpan="5" 
                        style={{ 
                          textAlign: 'center', 
                          padding: '2rem', 
                          color: '#999',
                          fontStyle: 'italic'
                        }}
                      >
                        No certificates found matching your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p style={{ 
              marginTop: '1.5rem', 
              textAlign: 'center', 
              color: '#666',
              fontSize: '0.9em'
            }}>
              Total Certificates: {sortedData.length}
            </p>
          </>
        )}
      </div>
    </>
  );
}